import { isUsingNewLayout } from '@app/adapters/silverfin'
import { DividerClasses } from '@app/shared/constants'

/**
 * The class names the communication cards render with, one set per layout.
 *
 * It carries class names only. A component that has to pick a different
 * component, prop value, or piece of markup per layout still branches on
 * `newLayout` itself.
 */
export interface CommsTheme {
  newLayout: boolean
  // Card shell
  cardBorder: string
  cardSubtleBackground: string
  cardSlimHover: string
  actionsOverlay: string
  // Message body
  authorText: string
  commentAuthorText: string
  bodyText: string
  doneByText: string
  chipBackground: string
  periodBorder: string
  // Replies
  commentsBandBackground: string
  commentDivider: string
  markAsReadText: string
  // Document pills
  pillText: string
  pillBorder: string
  pillDivider: string
  // Editor
  editorBorder: string
  toolbarBackground: string
  toolbarButton: string
  toolbarButtonOutline: string
  editorPopoverBackground: string
  toolbarShowsSeparators: boolean
  toolbarBottomBorder: string
  // To-do toggle
  todoRing: string
  todoRingOffset: string
  todoRingOffsetHover: string
  todoRingLocked: string
  todoRingOffsetLocked: string
  todoCheck: string
  todoFill: string
  todoFillDoneLocked: string
  // Reply form
  replyFooterBackground: string
  replyBoxBackground: string
  notificationInset: string
  dropdownBorder: string
  dropdownListBorder: string
  dropdownHeaderBackground: string
  dropdownText: string
}

const OLD: Omit<CommsTheme, 'newLayout'> = {
  cardBorder: 'tw-border-gray-300',
  cardSubtleBackground: 'tw-bg-sf-snow-subtle',
  cardSlimHover: 'hover:tw-bg-sf-snow-light',
  actionsOverlay:
    'tw-bg-sf-snow-light tw-shadow-[0_0_12px_12px_theme(colors.sf-snow.light)] hover:tw-shadow-[0_0_12px_12px_theme(colors.sf-snow.light)]',
  authorText: '',
  commentAuthorText: 'tw-text-neutral-800',
  bodyText: 'tw-text-sf-gray',
  doneByText: 'tw-text-sf-dark-dark',
  chipBackground: 'sf-bg-neutral-subtle-2',
  periodBorder: 'tw-border-sf-neutral-light-1',
  commentsBandBackground: 'tw-bg-sf-neutral-subtle-2',
  commentDivider: 'tw-border-sf-neutral-lighter-2',
  markAsReadText: 'tw-text-sf-gray-darker',
  pillText: 'tw-text-sf-gray-darker',
  pillBorder: 'tw-border-sf-neutral-lighter-1',
  pillDivider: 'tw-bg-sf-neutral-lighter-1',
  editorBorder: 'tw-border-sf-snow-darker',
  toolbarBackground: 'tw-bg-sf-snow',
  toolbarButton:
    'tw-rounded tw-p-1.5 tw-bg-transparent tw-border-none tw-shadow-none tw-text-sf-neutral-dark-1 hover:tw-bg-sf-snow-dark',
  toolbarButtonOutline: 'tw-outline-sf-snow-darker',
  editorPopoverBackground: 'tw-bg-sf-snow-light',
  toolbarShowsSeparators: true,
  toolbarBottomBorder:
    'tw-border tw-border-solid tw-border-sf-snow-darker tw-border-t-0 tw-border-b-1 tw-border-l-0 tw-border-r-0',
  todoRing: 'tw-ring-sf-snow-lighter',
  todoRingOffset: 'tw-ring-offset-sf-neutral-light-1',
  todoRingOffsetHover: 'hover:tw-ring-offset-sf-neutral-light-2',
  todoRingLocked: 'tw-ring-sf-snow',
  todoRingOffsetLocked: 'tw-ring-offset-sf-snow-dark',
  todoCheck: 'tw-text-sf-snow-lighter',
  todoFill: 'tw-bg-sf-snow',
  todoFillDoneLocked: 'tw-bg-sf-snow-darker',
  replyFooterBackground: 'tw-bg-sf-snow-light',
  replyBoxBackground: '',
  notificationInset: '',
  dropdownBorder: 'tw-border-sf-neutral-lighter-1',
  dropdownListBorder: 'tw-border-sf-neutral-lighter-2',
  dropdownHeaderBackground: 'tw-bg-sf-snow',
  dropdownText: 'tw-text-sf-neutral-dark-1',
}

const NEW: Omit<CommsTheme, 'newLayout'> = {
  cardBorder: 'tw-border-sf-slate-lighter-1',
  cardSubtleBackground: 'tw-bg-sf-slate-subtle-1',
  cardSlimHover: 'hover:tw-bg-sf-slate-subtle-1',
  actionsOverlay:
    'tw-bg-sf-slate-subtle-1 tw-shadow-[0_0_12px_12px_theme(colors.sf-slate.subtle-1)] hover:tw-shadow-[0_0_12px_12px_theme(colors.sf-slate.subtle-1)]',
  authorText: 'tw-text-sf-slate-dark-2',
  commentAuthorText: 'tw-text-sf-slate-dark-2',
  bodyText: 'tw-text-sf-slate-dark-1',
  doneByText: 'tw-text-sf-slate-dark-1',
  chipBackground: 'tw-bg-sf-slate-subtle-1',
  periodBorder: 'tw-border-sf-slate-lighter-1',
  commentsBandBackground: 'tw-bg-sf-slate-subtle-1',
  commentDivider: 'tw-border-sf-slate-lighter-1',
  markAsReadText: 'tw-text-sf-slate-dark-1',
  pillText: 'tw-text-sf-slate-dark-1',
  pillBorder: 'tw-border-sf-slate-lighter-1',
  pillDivider: 'tw-bg-sf-slate-lighter-1',
  editorBorder: 'tw-border-sf-slate-lighter-1',
  toolbarBackground: 'tw-bg-sf-slate-subtle-1',
  toolbarButton:
    'tw-w-6 tw-h-6 !tw-p-0 !tw-leading-6 tw-rounded tw-bg-transparent tw-border-none tw-shadow-none tw-text-sf-slate-dark-1 hover:tw-bg-sf-slate-lighter-1',
  toolbarButtonOutline: 'tw-outline-sf-slate-lighter-1',
  editorPopoverBackground: 'tw-bg-sf-slate-subtle-1',
  toolbarShowsSeparators: false,
  toolbarBottomBorder: DividerClasses.BOTTOM,
  todoRing: 'tw-ring-sf-slate-white',
  todoRingOffset: 'tw-ring-offset-sf-slate-light-1',
  todoRingOffsetHover: 'hover:tw-ring-offset-sf-slate-light-2',
  todoRingLocked: 'tw-ring-sf-slate-subtle-2',
  todoRingOffsetLocked: 'tw-ring-offset-sf-slate-lighter-1',
  todoCheck: 'tw-text-sf-slate-white',
  todoFill: 'tw-bg-sf-slate-subtle-2',
  todoFillDoneLocked: 'tw-bg-sf-slate-lighter-2',
  replyFooterBackground: 'tw-bg-sf-slate-subtle-1',
  replyBoxBackground: 'tw-bg-sf-slate-subtle-1',
  notificationInset: 'tw-px-2 tw-py-2',
  dropdownBorder: 'tw-border-sf-slate-lighter-1',
  dropdownListBorder: 'tw-border-sf-slate-lighter-1',
  dropdownHeaderBackground: 'tw-bg-sf-slate-subtle-1',
  dropdownText: 'tw-text-sf-slate-dark-1',
}

export function commsTheme(): CommsTheme {
  const newLayout = isUsingNewLayout()

  return { newLayout, ...(newLayout ? NEW : OLD) }
}
